import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { message } from 'antd';
import Inherit from '../../../core/inherit';
import ClockoutController from '../../../core/clockout.controller';
import MultimediaController from '../../support/multimedia.controller';
import MoneyController from '../../../core/money.controller';
import { UserActionsDelegate, UserActionsController, USER_ACTIONS } from '../../common/userActions';
import { SupportTicketConstants } from '../../support/support.enums';
import { DEFAULT_PAGE_SIZE, SYNC_DETAIL_STATUS, SYNC_DETAIL_ERROR } from '../../reports/reports.constants';
import { SYNC_REPORT_NONE } from '../../reports/elements/syncStatus.constants';
import { SCOPES } from '../../../constants/scopes.constants';
import { buildLedgerParams, getLedgerStateFromUrl } from '../../../utils/ledger';
import { LEDGER_LOAD_TOAST } from '../../common/ledgerTable/ledgerTable.constants';
import { resolveFunnelError } from './userFunnel.constants';
import SyncStateCatalogController from '../../common/statusClassification/syncStateCatalog.controller';
import UserDelegate from './user.delegate';
import { formatTimestampWithAge } from '../../../utils/datetime';
import {
    PLATFORM_DISPLAY_NAME_UNSET,
    UNASSIGNED_CONNECTION_KEY,
} from '../../common/payrollConnections/payrollConnections.constants';
import { CONNECTION_EVENTS_PER_PAGE } from './user.constants';
import {
    resolveLastSuccessfulConnection,
    resolveSyncStatusConnectionKey,
} from './payrollConnections.utils';

// parsePayPeriod uses explicit DD-MM-YYYY format strings — customParseFormat
// is required or dayjs ignores the format (native parse → Invalid Date for
// most pay-period boundaries, or wrong month/day when it happens to match).
// advancedFormat supplies ordinal labels ("May 1st") for the derived chips.
dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);

/**
 * Controller for the User Journey page (`/users/user?userId=...`).
 *
 * Owns:
 *  - Initial fetch of the consumer payload + payroll sync report.
 *  - Action handlers for the header (Open SSO, Restrict Access).
 *  - Action handlers for the Payroll Sync Status card
 *    (Mark Sync Complete, Delete Last Sync).
 *  - Pure derivation helpers (`formatUserNameHighlight`,
 *    `parsePayPeriod`) consumed by the view layer. Kept on the
 *    controller so the view stays declarative and the same logic is
 *    trivially callable from any handler that also needs it.
 *
 * Money colour and formatting come from `MoneyController` in the
 * Inherit chain, so the cards and the transactions table on this page
 * read one set of rules rather than each picking their own.
 *
 * Action handlers mirror the user-feedback style used by
 * `ReportsController.openSsoForUser` / `restrictUser` so both
 * surfaces emit identical toasts when admins act on the same
 * underlying endpoints.
 */
class UserController extends Inherit(ClockoutController, MultimediaController, MoneyController, UserActionsDelegate, UserActionsController, SyncStateCatalogController, UserDelegate) {

    /**
     * `ClockoutController.componentWillUnmount` calls `cleanup()`; mark the
     * page unmounted so late catalog / report responses do not `setState` on
     * a page that is gone (see SyncStateCatalogController).
     */
    async cleanup() {
        this._unmounted = true;
        if (typeof super.cleanup === 'function') await super.cleanup();
    }
    /**
     * Splits a user's full name into `{ head, tail }` so the header
     * can render the trailing portion in the brand-blue highlight
     * colour (Figma 5747:1690 — "Juan **Jurado-Blanco**"). The split
     * happens at the FIRST whitespace so multi-token surnames stay
     * together as `tail`. Returns `{ head: name, tail: '' }` when the
     * name has no whitespace, which still renders correctly via a
     * single text node.
     *
     * @param {string | null | undefined} name
     * @returns {{ head: string, tail: string }}
     */
    formatUserNameHighlight(name) {
        const safe = (name || '').trim();
        if (!safe) return { head: '', tail: '' };
        const firstSpace = safe.indexOf(' ');
        if (firstSpace === -1) return { head: safe, tail: '' };
        return {
            head: safe.slice(0, firstSpace),
            tail: safe.slice(firstSpace + 1),
        };
    }

    /**
     * Parses the `payPeriod` string returned by `fetchConsumerUser`
     * (shape: "DD-MM-YYYY HH:mm:ss to DD-MM-YYYY HH:mm:ss") into the
     * three labels consumed by the page:
     *
     *  - `rangeChipLabel`   — uppercase chip in the header
     *    ("Pay Period May 1st - May 14th").
     *  - `payCycleLabel`    — Pay Cycle row in User Details
     *    ("May 1st - May 14th").
     *  - `nextPaydayLabel`  — Next Payday row in User Details
     *    ("May 15th"). Always end-date + 1 day (the payday is the day
     *    after the pay-period ends).
     *
     * Returns `null` when `payPeriod` is missing or unparseable so
     * the consumer can render a placeholder without try/catching.
     *
     * @param {string | null | undefined} payPeriod
     * @returns {{
     *   rangeChipLabel: string,
     *   payCycleLabel: string,
     *   nextPaydayLabel: string,
     * } | null}
     */
    parsePayPeriod(payPeriod) {
        if (!payPeriod || typeof payPeriod !== 'string' || !payPeriod.includes(' to ')) {
            return null;
        }
        const [startPart, endPart] = payPeriod.split(' to ');
        const startKey = (startPart || '').split(' ')[0];
        const endKey = (endPart || '').split(' ')[0];
        const start = dayjs(startKey, 'DD-MM-YYYY');
        const end = dayjs(endKey, 'DD-MM-YYYY');
        if (!start.isValid() || !end.isValid()) return null;

        const startShort = start.format('MMM Do');
        const endShort = end.format('MMM Do');
        const nextPayday = end.add(1, 'day').format('MMM Do');

        return {
            rangeChipLabel: `Pay Period ${startShort} - ${endShort}`,
            payCycleLabel: `${startShort} - ${endShort}`,
            nextPaydayLabel: nextPayday,
        };
    }

    /**
     * The member this page is about. Every handler needs it and every handler
     * used to re-read it inline; kept as a getter so `userActionConfirm`'s
     * clientId and the handlers cannot disagree about which member is targeted.
     */
    get userActionClientId() {
        return this.props.searchParams.get('userId');
    }

    canOpenSso() {
        return this.hasScope(SCOPES.SSO_OPEN);
    }

    /**
     * Restrict / restore this member's platform access. Its own scope, and read
     * through the controller's hasScope() (which fails closed on an unknown
     * key) rather than the userHasScope() helper, whose `!scope -> true` makes
     * a mistyped constant show the button to everyone.
     */
    canRestrictUser() {
        return this.hasScope(SCOPES.USERS_RESTRICT);
    }

    canDeleteSync() {
        return this.hasScope(SCOPES.SYNC_DELETE_LATEST);
    }

    canMarkSyncComplete() {
        return this.hasScope(SCOPES.SYNC_MARK_COMPLETE);
    }

    canReconcileAccount() {
        return this.hasScope(SCOPES.USERS_RECONCILE);
    }

    /**
     * Both funnel reads are gated on the reports-screen scope — the same gate
     * the per-member report already sits behind, and NOT the team-invite scope
     * the page's write actions use (see AdminUserFunnel in clockout-service).
     * Checked client-side too so an agent without it sees the page without the
     * card, rather than a card that always fails with a 403.
     */
    canViewFunnel() {
        return this.hasScope(SCOPES.REPORTS_VIEW);
    }

    async initialize() {
        if (!this.user || !this.user.access || this.user.access.length === 0) {
            this.navigateLogin();
            return;
        }

        const clientId = this.userActionClientId;
        if (!clientId) {
            message.error('No user selected.');
            this.props.navigate?.('/reports');
            this.setState({ loading: false });
            return;
        }

        // Started BEFORE the detail payload is awaited, not after it resolves.
        // The two requests are independent, and the table has its own spinner —
        // sequencing it behind the consumer payload only made the rows arrive a
        // round trip late for no gain.
        //
        // Seed page, rows-per-page, sort and the type filter from the query
        // string so a refresh or a shared link lands on the same slice. The
        // design system's Table is what put them there.
        const { page, perPage, sort, type } = getLedgerStateFromUrl({ defaultPageSize: DEFAULT_PAGE_SIZE });
        this.setState({ transactionsSort: sort, transactionsType: type });
        const transactions = this.loadTransactions(page, perPage, { sort, type });

        // The sync report loads concurrently with the consumer payload and is
        // never awaited here: its card owns a bounded loading / error state,
        // and the transactions table above must not wait on it.
        this.reloadSyncStatus();
        // Status colours come from the served classification (KAN-618); cached
        // per session, so this is a no-op after the Reports screen loaded it.
        this.loadSyncStateCatalog();
        this.reloadPayrollConnections();


        try {
            const consumer = await this.data.fetchConsumerUser(clientId);
            const safeConsumer = consumer || {};

            this.setState({
                loading: false,
                user: this.data.user,
                consumer: safeConsumer,
                isRestricted: safeConsumer?.restricted === true || safeConsumer?.accountActive === false,
                // Everything above belongs to this load alone; the connections
                // slice is shared, so it is only written while this read is
                // still the newest one.
            });
        }
        catch (error) {
            console.error('Error loading user detail:', error);
            message.error('Failed to load user details. Please try again.');
            this.setState({ loading: false });
        }

        // `loadTransactions` owns its own error handling, so this is only
        // awaited to keep `initialize` from resolving while a fetch it started
        // is still in flight.
        await transactions;
        // Not awaited, and deliberately last: the funnel is an Athena scan and
        // is by far the slowest read on this page. Awaiting it here would hold
        // the whole detail view behind it for no reason.
        this.loadFunnel();
    }

    /**
     * Fetch this member's onboarding journey. Owns its own loading/error state
     * because the card renders them itself — the rest of the page is already
     * usable while this is in flight.
     */
    async loadFunnel() {
        const clientId = this.props.searchParams.get('userId');
        if (!clientId || !this.canViewFunnel()) return;

        // Every write below is guarded by this token. Both entry points — the
        // page load and the card's retry — come through here, so without it a
        // second click while the first scan is still running lets the first
        // request's `finally` clear `funnelLoading` underneath it: the card
        // then has no error and no rows and renders "No journey events
        // recorded for this member yet", which is a claim about the member
        // made while a request is in flight.
        this._funnelToken = (this._funnelToken || 0) + 1;
        const token = this._funnelToken;
        const isCurrent = () => this._funnelToken === token;

        // `funnelRetryable` is reset alongside the error it qualifies: it is only
        // ever written in the catch, so a 400/403 would otherwise leave the retry
        // suppressed for every later attempt on this mounted page — including one
        // that succeeded and then failed on a 500, which IS retryable.
        this.setState({ funnelLoading: true, funnelError: null, funnelRetryable: true });
        try {
            const funnel = await this.data.fetchUserFunnel(clientId);
            if (isCurrent()) this.setState({ funnel: funnel || null });
        }
        catch (error) {
            console.error('Error loading user funnel:', error);
            const { message: funnelError, retryable } = resolveFunnelError(error?.response?.status);
            if (isCurrent()) this.setState({ funnel: null, funnelError, funnelRetryable: retryable });
        }
        finally {
            if (isCurrent()) this.setState({ funnelLoading: false });
        }
    }

    /**
     * Open the payroll-link failure history and load it.
     *
     * A second Athena scan, so it is deliberately not part of the page load:
     * an agent opens the error row far less often than they open a member.
     * Re-fetched on every open rather than cached — the previous answer
     * belongs to whichever member was on screen when it was fetched, and a
     * stale failure list is worse than a spinner.
     */
    openLinkAttempts() {
        const clientId = this.props.searchParams.get('userId');
        if (!clientId || !this.canViewFunnel()) return;

        this.setState({ linkAttemptsOpen: true });
        this.loadLinkAttempts();
    }

    /**
     * The read behind the modal, separate from `openLinkAttempts` so the
     * modal's own retry re-runs exactly the same load instead of reopening a
     * modal that is already open.
     *
     * `has_error` is kept alongside the list: the endpoint sends it so the
     * client can tell "this member had no linking failures" apart from "the
     * history came back empty", and those are different sentences — this modal
     * is only reachable from an error row, so an empty list beside
     * `has_error: true` means the failures could not be reconstructed, not
     * that there were none.
     */
    async loadLinkAttempts() {
        const clientId = this.props.searchParams.get('userId');
        if (!clientId || !this.canViewFunnel()) return;

        // Same guard as `loadFunnel`, for the same reason: open / close / open
        // and the modal's retry all land here, and a late response from an
        // earlier open would otherwise clear the spinner out from under the
        // one the agent is actually waiting on.
        this._linkAttemptsToken = (this._linkAttemptsToken || 0) + 1;
        const token = this._linkAttemptsToken;
        const isCurrent = () => this._linkAttemptsToken === token;

        this.setState({
            linkAttemptsLoading: true,
            linkAttemptsError: null,
            linkAttemptsRetryable: true,
        });
        try {
            const payload = await this.data.fetchUserLinkAttempts(clientId);
            if (isCurrent()) {
                this.setState({
                    linkAttempts: payload?.attempts || [],
                    linkAttemptsHasError: Boolean(payload?.has_error),
                });
            }
        }
        catch (error) {
            console.error('Error loading link attempts:', error);
            const { message, retryable } = resolveFunnelError(
                error?.response?.status, 'payroll connection attempts'
            );
            if (isCurrent()) {
                this.setState({
                    linkAttempts: null,
                    linkAttemptsError: message,
                    linkAttemptsRetryable: retryable,
                });
            }
        }
        finally {
            if (isCurrent()) this.setState({ linkAttemptsLoading: false });
        }
    }

    closeLinkAttempts() {
        this.setState({ linkAttemptsOpen: false });
    }

    /**
     * Fetch one page of transactions for this user from the ledger endpoint,
     * filtered by `clientId`. Called on mount and on every pagination, sort or
     * filter change. Sorting and filtering are server-side — the endpoint
     * paginates, so the browser only ever holds one page.
     *
     * Every response is checked against `transactionsRequestId` before it is
     * applied. Page, sort and filter all enter through this one path, and the
     * table can be re-sorted before the previous fetch answers — without the
     * token a slow first response lands after a fast second one and the rows on
     * screen no longer match the headers or the URL claiming to describe them.
     *
     * @param {number} [page=1]
     * @param {number} [perPage=DEFAULT_PAGE_SIZE]
     * @param {{sort?: object, type?: string|null}} [options] defaults to current state
     */
    async loadTransactions(page = 1, perPage = DEFAULT_PAGE_SIZE, options = {}) {
        const clientId = this.props.searchParams.get('userId');
        if (!clientId) return;

        // `!== undefined`, not `??`: null is a VALUE here, not an absence. Antd
        // clears the sort on the third header click, the handler reads that
        // back as `sort: null` and passes it — and `??` would have fallen
        // through to `this.state`, which React has not updated yet (the
        // handler's `setState` batches). The stale sort would then reach the
        // endpoint while the headers showed none: exactly the disagreement
        // between URL and fetched slice this path exists to prevent.
        const sort = options.sort !== undefined ? options.sort : this.state.transactionsSort;
        const type = options.type !== undefined ? options.type : this.state.transactionsType;

        const requestId = (this.transactionsRequestId || 0) + 1;
        this.transactionsRequestId = requestId;
        const isCurrent = () => this.transactionsRequestId === requestId;

        this.setState({ transactionsLoading: true, transactionsFailed: false });
        try {
            const ledger = await this.data.fetchLedger(
                buildLedgerParams({ clientId, page, perPage, sort, type }),
            );
            if (!isCurrent()) return;
            const items = ledger?.items || [];
            const total = ledger?.pageinfo?.count ?? items.length;
            this.setState({
                transactions: items,
                transactionsPageInfo: { current: page, perPage, total },
                // The zone the endpoint evaluated its dates in (KAN-576). The
                // table renders and labels in it, falling back to the ledger
                // default until the service that declares it is deployed.
                transactionsTimezone: ledger?.timezone ?? null,
            });
        }
        catch (error) {
            console.error('Error loading transactions:', error);
            if (!isCurrent()) return;
            // The rows on screen belong to the previous slice, so leaving them
            // under the new headers would show an order the server never
            // applied. Clear them and say so — an empty table with no message
            // reads as "this member has no transactions".
            this.setState({
                transactions: [],
                transactionsFailed: true,
                transactionsPageInfo: { current: page, perPage, total: 0 },
            });
            message.error(LEDGER_LOAD_TOAST.FAILED);
        }
        finally {
            if (isCurrent()) this.setState({ transactionsLoading: false });
        }
    }

    /**
     * Re-fetch the slice the URL currently describes, for the "Try again"
     * control the table shows when a fetch failed.
     *
     * Reads the state back rather than remembering what failed: the URL is the
     * one writer, so a retry lands on exactly the page, sort and filter the
     * headers are showing.
     */
    retryTransactions() {
        const { page, perPage, sort, type } = getLedgerStateFromUrl({ defaultPageSize: DEFAULT_PAGE_SIZE });
        this.setState({ transactionsSort: sort, transactionsType: type });
        return this.loadTransactions(page, perPage, { sort, type });
    }

    /**
     * Antd `onChange` for the transactions table.
     *
     * By the time this runs the Table has already written the new page, sort and
     * filters to the query string (`saveSearchParams`), including the reset to
     * page 1 whenever a sort or a filter re-slices the set. So this reads that
     * state back instead of re-deriving it from antd's arguments: one writer,
     * one reader, and no way for the URL and the fetch to disagree.
     */
    handleTransactionsTableChange() {
        const { page, perPage, sort, type } = getLedgerStateFromUrl({ defaultPageSize: DEFAULT_PAGE_SIZE });
        this.setState({ transactionsSort: sort, transactionsType: type });
        this.loadTransactions(page, perPage, { sort, type });
    }


    /**
     * Re-fetch only the sync report. Used after admin actions that
     * mutate the latest sync row (Mark Complete / Delete Last) so
     * the card's pill colours reflect the new state without a full
     * page reload.
     */
    async reloadSyncStatus() {
        const clientId = this.userActionClientId;
        if (!clientId) return;
        // Generation counter (as on the Reports side): a slow initial load
        // must not land after an action's fresh reload and overwrite it.
        const generation = (this._syncDetailGeneration || 0) + 1;
        this._syncDetailGeneration = generation;
        // Keep the previous report while reloading so the card's Overall pill
        // and the work-type label do not blank (or read "Unavailable").
        this.setState((prev) => ({
            syncDetail: { status: SYNC_DETAIL_STATUS.LOADING, report: prev.syncDetail?.report ?? null, error: null },
        }));
        try {
            const payload = await this.data.fetchUserSyncStatus(clientId);
            if (this._syncDetailGeneration !== generation) return; // stale
            // Same rule as the Reports panel: an empty 200 body is a
            // never-synced member ("No Sync"), not a load failure.
            this.setState({ syncDetail: { status: SYNC_DETAIL_STATUS.READY, report: payload || SYNC_REPORT_NONE, error: null } });
        }
        catch (error) {
            if (this._syncDetailGeneration !== generation) return; // stale
            console.error('Error reloading sync status:', error);
            this.setState({
                syncDetail: {
                    status: SYNC_DETAIL_STATUS.ERROR,
                    report: null,
                    error: error?.reason === SYNC_DETAIL_ERROR.TIMEOUT ? SYNC_DETAIL_ERROR.TIMEOUT : SYNC_DETAIL_ERROR.LOAD_FAILED,
                },
            });
        }
    }


    /**
     * Re-reads the payroll-connection summary (KAN-610). Fire-and-forget on
     * init like `reloadSyncStatus`, and used by the panel's Retry and after the
     * two sync admin actions, both of which change what the summary reports.
     * A failure surfaces inside the panel, never as a page error.
     */
    async reloadPayrollConnections() {
        const clientId = this.userActionClientId;
        if (!clientId) return;

        // Retry and the post-action refresh can overlap; only the newest read
        // is allowed to write, or a slow earlier one lands last and restores
        // the summary the admin just changed.
        const generation = this.bumpPayrollConnectionsGeneration();

        this.setPayrollConnections({ loading: true, error: null });
        try {
            const payload = await this.data.fetchUserPayrollConnections(clientId);
            if (this._unmounted || !this.isCurrentPayrollConnectionsGeneration(generation)) return;
            this.setPayrollConnections({
                loading: false,
                error: null,
                data: payload?.connections || [],
                timezone: payload?.timezone || null,
            });
        } catch (error) {
            console.error('Error loading payroll connections:', error);
            if (this._unmounted || !this.isCurrentPayrollConnectionsGeneration(generation)) return;
            this.setPayrollConnections({ loading: false, error });
        }
    }

    /**
     * Loads one page of a single connection's event history.
     *
     * The fetch-once guard lives here rather than in the panel so that
     * collapsing and re-expanding costs nothing, while a *failed* first load
     * stays retryable: an errored slice has no `items`, so the guard below
     * falls through and the request runs again. Only the addressed
     * connection's slice is touched — expanding a second job never refetches
     * the first.
     *
     * @param {string} connectionKey A `connection_key` ("unassigned" included).
     * @param {number} [page]
     */
    async loadConnectionEvents(connectionKey, page = 1) {
        const clientId = this.userActionClientId;
        if (!clientId || !connectionKey) return;

        const slice = this.state.connectionEvents?.[connectionKey];
        if (slice?.loading || slice?.loadingMore) return;
        if (page === 1 && slice && !slice.error && slice.items) return;

        const isFirstPage = page === 1;
        // Stamped before the request so an admin action that invalidates this
        // connection mid-flight (`clearConnectionEvents` bumps the token) can
        // drop the answer.
        const generation = this.bumpConnectionEventsGeneration(connectionKey);
        this.setConnectionEventsSlice(connectionKey, {
            error: null,
            moreError: null,
            loading: isFirstPage,
            loadingMore: !isFirstPage,
        });

        try {
            const payload = await this.data.fetchPayrollConnectionEvents(clientId, connectionKey, {
                page,
                perPage: CONNECTION_EVENTS_PER_PAGE,
            });
            if (!this.isCurrentConnectionEventsGeneration(connectionKey, generation)) return;
            const items = payload?.items || [];
            const pageinfo = payload?.pageinfo || null;

            if (isFirstPage) {
                this.setConnectionEventsSlice(connectionKey, {
                    items,
                    pageinfo,
                    loading: false,
                    loadingMore: false,
                    error: null,
                    moreError: null,
                });
                return;
            }
            this.appendConnectionEvents(connectionKey, items, pageinfo);
        } catch (error) {
            console.error('Error loading payroll connection events:', error);
            if (!this.isCurrentConnectionEventsGeneration(connectionKey, generation)) return;
            // A failed FIRST page has nothing to fall back on, so it owns the
            // whole surface. A failed LATER page must not replace the rows the
            // reader is part-way through.
            this.setConnectionEventsSlice(connectionKey, {
                loading: false,
                loadingMore: false,
                ...(isFirstPage ? { error } : { moreError: error }),
            });
        }
    }

    /** Fired on every expand; `loadConnectionEvents` decides if that costs a request. */
    handleExpandConnection(connectionKey) {
        return this.loadConnectionEvents(connectionKey, 1);
    }

    /**
     * Records which connection panels are open, and loads the history of one
     * that just opened. The open set lives here because the page reads it:
     * `resolveSyncStatusHost` decides whether the sync card — and the two admin
     * actions on it — is reachable inside a panel or has to render standalone.
     */
    handleToggleConnection(connectionKey, expanded) {
        this.setConnectionExpanded(connectionKey, expanded);
        if (!expanded) {
            return undefined;
        }
        return this.handleExpandConnection(connectionKey);
    }

    /** Retry after a failed history load — same entry point, guard now falls through. */
    handleRetryConnectionEvents(connectionKey) {
        return this.loadConnectionEvents(connectionKey, 1);
    }

    /** "Show more": the next page for this connection only, appended. */
    handleLoadMoreConnectionEvents(connectionKey) {
        const slice = this.state.connectionEvents?.[connectionKey];
        const currentPage = Number(slice?.pageinfo?.page) || 1;
        return this.loadConnectionEvents(connectionKey, currentPage + 1);
    }

    /**
     * The at-a-glance payroll line for the page header: the most recent
     * successful connection across every connection the member has. Explicit
     * "Never connected" copy rather than a placeholder date, and `null` while
     * the summary is loading or unreadable so the header stays quiet.
     *
     * @returns {{ text: string, platformLabel: string | null } | null}
     */
    resolvePayrollHeaderSummary() {
        const { payrollConnections } = this.state;
        if (!payrollConnections || payrollConnections.loading || payrollConnections.error) {
            return null;
        }
        if (!Array.isArray(payrollConnections.data)) {
            return null;
        }

        const last = resolveLastSuccessfulConnection(payrollConnections.data);
        if (!last) {
            return { text: 'Never connected', platformLabel: null };
        }

        // "Not set" is the right title for a connection card and the wrong
        // value for a header chip, where it would read as a platform the member
        // is on; an unset platform stays absent and `consumer.payrollSystem`
        // gets to answer.
        const platformLabel = last.platformLabel === PLATFORM_DISPLAY_NAME_UNSET ? null : last.platformLabel;
        const when = formatTimestampWithAge(last.at);
        return {
            text: platformLabel ? `${when} · ${platformLabel}` : when,
            platformLabel,
        };
    }

    /**
     * Where the per-dimension sync card should render. `placed` is false when
     * no connection panel can host it (summary failed, or its account is not in
     * the summary); the card then renders standalone so the two admin actions
     * on it stay reachable.
     *
     * @returns {{ key: string | null, placed: boolean }}
     */
    resolveSyncStatusPlacement() {
        const key = resolveSyncStatusConnectionKey(this.state.syncDetail?.report);
        if (!key) {
            return { key: null, placed: false };
        }
        const connections = this.state.payrollConnections?.data;
        const placed =
            Array.isArray(connections) &&
            connections.some((connection) => (connection?.connection_key ?? UNASSIGNED_CONNECTION_KEY) === key);
        return { key, placed };
    }

    /**
     * Whether the sync card is *currently* rendered inside a connection panel:
     * a collapsed host renders nothing, so the standalone fallback has to fire
     * while the host is shut, not only when there is no host.
     *
     * @returns {{ key: string | null, placed: boolean, hosted: boolean }}
     */
    resolveSyncStatusHost() {
        const { key, placed } = this.resolveSyncStatusPlacement();
        return { key, placed, hosted: placed && this.isConnectionExpanded(key) };
    }

    /**
     * Re-syncs the payroll panel after an admin action mutated a `sync` row:
     * drops the affected connection's cached events, re-reads the summary, and
     * refetches an open panel immediately so it goes from rows to rows.
     *
     * @param {string | null} connectionKey
     */
    async refreshConnectionAfterSyncAction(connectionKey) {
        if (connectionKey) {
            this.clearConnectionEvents(connectionKey);
        }
        await this.reloadPayrollConnections();
        if (connectionKey && this.isConnectionExpanded(connectionKey)) {
            await this.loadConnectionEvents(connectionKey, 1);
        }
    }

    /**
     * Ask before entering a member's account. The warning is not decoration:
     * everything the admin does in that session is a real action attributed to
     * the member, and it is audited against the admin's name.
     */
    handleOpenSso() {
        const clientId = this.userActionClientId;
        if (!clientId) return;
        this.requestUserAction(USER_ACTIONS.SSO, clientId, { name: this.state.consumer?.name });
    }

    /**
     * Open the consumer app (rn-ui) for the member in a new tab. Same-origin
     * admin route, so the auth cookie carries over — no separate login.
     *
     * This does NOT resolve an SSO session: OpenSsoView resolves its own on the
     * other side. Resolving one here too would mint two sessions and write two
     * `admin.sso.session_opened` audit rows for a single click.
     *
     * `window.open` must stay synchronous inside the click gesture — Safari and
     * Firefox block a popup opened after an await. Nothing may be awaited on the
     * path from the modal's OK to this line.
     */
    performOpenSso(clientId) {
        window.open(`/open-sso/${encodeURIComponent(clientId)}`, '_blank');
    }

    /**
     * Ask before cutting off a member's access. This page used to fire straight
     * from the button with no confirmation at all, unlike the Reports row,
     * which always had one.
     *
     * Restoring stays single-click — see ReportsController.restrictUser.
     */
    handleToggleRestrict() {
        const clientId = this.userActionClientId;
        if (!clientId) return;

        const { isRestricted } = this.state;
        if (isRestricted) {
            this.performRestrict(clientId, isRestricted);
            return;
        }

        this.requestUserAction(USER_ACTIONS.RESTRICT, clientId, {
            name: this.state.consumer?.name,
        });
    }

    /**
     * Run the confirmed action. The half that genuinely differs per page: this
     * page opens a tab and refetches one member; Reports embeds the app inline
     * and reloads a table.
     * @param {'sso'|'restrict'} action
     * @param {string} clientId
     * @returns {Promise<boolean>} false leaves the confirmation open.
     */
    async runUserAction(action, clientId) {
        if (action === USER_ACTIONS.SSO) {
            // Synchronous — see performOpenSso on why nothing may await here.
            this.performOpenSso(clientId);
            return true;
        }
        // Always the restrict direction: restore never opens this modal, so
        // the member is by definition still unrestricted here.
        return this.performRestrict(clientId, false);
    }

    /**
     * Toggle the user's `account.active` flag. Restricts the user
     * when currently active, restores access when currently
     * restricted. The endpoint is idempotent so consecutive clicks
     * are safe.
     * @returns {Promise<boolean>} false on failure, so the modal stays open.
     */
    async performRestrict(clientId, isRestricted) {
        if (this.state.restrictLoading) return false;

        const nextActive = !!isRestricted;

        this.setState({ restrictLoading: true });
        try {
            const result = await this.data.restrictUser(clientId, nextActive);
            const newRestricted = result?.active === false;
            const refreshed = await this.data.fetchConsumerUser(clientId);
            this.setState({
                isRestricted: newRestricted,
                consumer: refreshed || this.state.consumer,
            });
            message.success(newRestricted
                ? 'User access restricted.'
                : 'User access restored.');
            return true;
        }
        catch (error) {
            console.error('Error toggling restrict:', error);
            const status = error?.response?.status;
            if (status === 403) message.error('You do not have access to do that for this FI.');
            else if (status === 404) message.error('User not found.');
            else message.error('Failed to update user access. Please try again.');
            return false;
        }
        finally {
            this.setState({ restrictLoading: false });
        }
    }

    /**
     * Force-mark the most recent (non-DD-switch) sync row as
     * COMPLETE for the current user. Refreshes the cached report so
     * the card reflects the new state.
     */
    async handleMarkSyncComplete() {
        const clientId = this.userActionClientId;
        if (!clientId) return;

        if (this.state.markCompleteLoading) return;


        this.setState({ markCompleteLoading: true });
        try {
            // Captured before the mutation: afterwards the report may point at a
            // different sync row, and the panel derives from the same rows.
            const affectedKey = resolveSyncStatusConnectionKey(this.state.syncDetail?.report);
            await this.data.markUserLatestSyncComplete(clientId);
            await this.reloadSyncStatus();
            await this.refreshConnectionAfterSyncAction(affectedKey);
            message.success('Sync row marked complete.');
        }
        catch (error) {
            console.error('Error marking sync complete:', error);
            const status = error?.response?.status;
            if (status === 403) message.error('You do not have access to do that for this FI.');
            else if (status === 404) message.error('No sync row to mark complete.');
            else message.error('Failed to mark sync row complete. Please try again.');
        }
        finally {
            this.setState({ markCompleteLoading: false });
        }
    }

    /**
     * Delete the most recent (non-DD-switch) sync row for the
     * current user, then refresh the report so the card reflects
     * the new latest row.
     */
    async handleDeleteLastSync() {
        const clientId = this.userActionClientId;
        if (!clientId) return;

        if (this.state.deleteLastLoading) return;


        this.setState({ deleteLastLoading: true });
        try {
            // Captured before the mutation: afterwards the report may point at a
            // different sync row, and the panel derives from the same rows.
            const affectedKey = resolveSyncStatusConnectionKey(this.state.syncDetail?.report);
            await this.data.deleteUserLatestSync(clientId);
            await this.reloadSyncStatus();
            await this.refreshConnectionAfterSyncAction(affectedKey);
            message.success('Latest sync row deleted.');
        }
        catch (error) {
            console.error('Error deleting latest sync:', error);
            const status = error?.response?.status;
            if (status === 403) message.error('You do not have access to do that for this FI.');
            else if (status === 404) message.error('No sync row to delete.');
            else message.error('Failed to delete sync row. Please try again.');
        }
        finally {
            this.setState({ deleteLastLoading: false });
        }
    }

    handleOpenReconcileModal() {
        this.setState({ reconcileModalOpen: true });
    }

    handleCloseReconcileModal() {
        // Never yank the modal out from under an in-flight request — the user
        // would be left unsure whether the settlement was written.
        if (this.state.reconcileLoading) return;
        this.setState({ reconcileModalOpen: false });
    }

    /**
     * Settle this user's entire outstanding debt with one aggregate ledger
     * adjustment. Refreshes the consumer payload and the transaction table —
     * the sync report is untouched by this action, so it is not reloaded.
     *
     * The modal is only closed on success: a failure leaves it open so the
     * error is read next to the action that caused it.
     */
    async handleConfirmReconcile() {
        const clientId = this.userActionClientId;
        if (!clientId) return;

        if (this.state.reconcileLoading) return;

        this.setState({ reconcileLoading: true });
        try {
            await this.data.reconcileUserAccount(clientId);

            const { current, perPage } = this.state.transactionsPageInfo || {};
            const [refreshed] = await Promise.all([
                this.data.fetchConsumerUser(clientId),
                this.loadTransactions(current || 1, perPage || DEFAULT_PAGE_SIZE),
            ]);

            this.setState({
                consumer: refreshed || this.state.consumer,
                reconcileModalOpen: false,
            });
            message.success('Account reconciled. All outstanding debt marked as settled.');
        }
        catch (error) {
            console.error('Error reconciling account:', error);
            const status = error?.response?.status;
            if (status === 403) message.error('You do not have access to do that for this FI.');
            else if (status === 404) message.error('User not found.');
            // 409 (nothing outstanding) and 424 (no pay period to settle
            // against) carry a message written for the admin — show it rather
            // than a generic failure.
            else message.error(error?.response?.data?.error || 'Failed to reconcile account. Please try again.');
        }
        finally {
            this.setState({ reconcileLoading: false });
        }
    }

    /**
     * Open the support ticket modal for a specific ledger transaction.
     * @param {Object} record
     */
    handleOpenTransactionTicket(record) {
        if (!record?.id) {
            message.error('This transaction cannot be linked to a ticket.');
            return;
        }
        const clientId = record.client_id || this.userActionClientId;
        this.setState({
            transactionTicketModalOpen: true,
            transactionTicketLedgerId: record.id,
            transactionTicketClientId: clientId,
            transactionTicketSubject: '',
            transactionTicketBody: '',
            transactionTicketCategory: null,
            transactionTicketPriority: null,
            transactionTicketSubmitting: false,
        });
    }

    handleCloseTransactionTicketModal() {
        const { transactionTicketAttachments } = this.state;
        for (const att of (transactionTicketAttachments || [])) {
            if (att.url?.startsWith('blob:')) URL.revokeObjectURL(att.url);
        }
        this.setState({
            transactionTicketModalOpen: false,
            transactionTicketSubmitting: false,
            transactionTicketLedgerId: null,
            transactionTicketClientId: null,
            transactionTicketSubject: '',
            transactionTicketBody: '',
            transactionTicketAttachments: [],
            transactionTicketCategory: null,
            transactionTicketPriority: null,
        });
    }

    async handleSubmitTransactionTicket() {
        const {
            transactionTicketSubject,
            transactionTicketBody,
            transactionTicketCategory,
            transactionTicketPriority,
            transactionTicketLedgerId,
            transactionTicketClientId,
            transactionTicketSubmitting,
        } = this.state;

        if (transactionTicketSubmitting) return;

        const subject = (transactionTicketSubject || '').trim();
        const body = (transactionTicketBody || '').trim();
        const attachments = this.state.transactionTicketAttachments ?? [];
        if (!subject || !body) {
            message.error('Subject and message are required.');
            return;
        }
        if (subject.length > SupportTicketConstants.SUBJECT_MAX_LENGTH) {
            message.error(`Subject must be at most ${SupportTicketConstants.SUBJECT_MAX_LENGTH} characters.`);
            return;
        }
        if (body.length > SupportTicketConstants.BODY_MAX_LENGTH) {
            message.error(`Message must be at most ${SupportTicketConstants.BODY_MAX_LENGTH} characters.`);
            return;
        }
        if (!transactionTicketLedgerId) {
            message.error('Missing transaction to link.');
            return;
        }

        this.setState({ transactionTicketSubmitting: true });
        try {
            // Validate and upload attachments before creating ticket
            let files = [];
            if (attachments.length > 0) {
                files = await this.uploadAttachments(attachments, {
                    requestPresigned: this.data.requestPresignedTicketUrl.bind(this.data),
                    uploadToS3: this.data.uploadFileToS3.bind(this.data)
                });
            }

            const result = await this.data.createTicket({
                subject,
                body,
                category: transactionTicketCategory,
                priority: transactionTicketPriority,
                ledgerIds: [transactionTicketLedgerId],
                client_id: transactionTicketClientId || undefined,
            }, files);
            const ticketId = result?.ticket?.id;
            message.success('Support ticket created.');
            this.handleCloseTransactionTicketModal();
            if (ticketId) {
                this.navigateTicket(ticketId);
            }
        } catch (error) {
            // Distinguish between upload and ticket creation failures
            if (error.code === 'UPLOAD_PARTIAL_FAILURE') {
                message.error(error.message + ' Please try again or contact support if files remain inaccessible.');
            } else if (error.code === 'UPLOAD_FAILED') {
                message.error(error.message || 'Could not upload attachments. Please try again.');
            } else {
                const desc = error?.response?.data?.error || 'Could not create ticket. Please try again.';
                message.error(desc);
            }
        } finally {
            this.setState({ transactionTicketSubmitting: false });
        }
    }
}

export default UserController;
